import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminEmptyStateComponent,
  AdminIconComponent,
  AdminSidePanelComponent,
  StatusBadgeComponent,
} from '../admin';
import { AdminToastService } from '../admin/admin-toast.service';
import { EmailModalComponent } from '../components/email-modal/email-modal.component';
import { LeadTimelineComponent } from '../components/lead-timeline/lead-timeline.component';
import { LeadProfileAvatarComponent } from '../components/lead-profile-avatar/lead-profile-avatar.component';
import { WorkflowTransitionModalComponent } from '../components/workflow-transition-modal/workflow-transition-modal.component';
import { WorkflowDocumentationModalComponent } from '../components/workflow-documentation-modal/workflow-documentation-modal.component';
import { WhatsappModalComponent } from '../components/whatsapp-modal/whatsapp-modal.component';
import { AuthService } from '../../services/auth.service';
import { PermissionService } from '../../platform/permissions/permission.service';
import { LeadFormFieldsComponent } from '../components/lead-form-fields/lead-form-fields.component';
import { LeadMessagePreviewComponent } from '../components/lead-message-preview/lead-message-preview.component';
import { LeadMergeModalComponent } from '../components/lead-merge-modal/lead-merge-modal.component';
import { CrmTasksSectionComponent } from '../components/crm-tasks-section/crm-tasks-section.component';
import {
  LEAD_SOURCE_OPTIONS,
  resolveLeadSource,
  type LeadSourceOption,
} from '../constants/lead-source.constants';
import {
  hasLeadFormFieldErrors,
  leadFormFromLead,
  leadFormToUpdatePayload,
  validateRequiredLeadFormFields,
  type LeadFormFieldErrors,
  type LeadFormFields,
} from '../models/lead-form.model';
import {
  LeadDto,
  LeadSubStatusOption,
  LeadTransitionOption,
  LeadsService,
  UpdateLeadPayload,
  WorkflowDocumentationStatus,
  WorkflowDocumentationStep,
} from '../services/leads.service';
import { TenantLeadsService } from '../services/tenant-leads.service';
import type { LeadMergeExecuteResult } from '../models/lead-merge.model';
import {
  CommunicationsService,
  type CommunicationChannel,
  type CommunicationDto,
  type Paginated,
} from '../services/communications.service';
import { CountriesService } from '../services/countries.service';
import { PlatformUsersService } from '../services/platform-users.service';
import { TenantUsersService } from '../services/tenant-users.service';
import { indianStateOptions, INDIAN_STATES } from '../constants/indian-states.constants';
import { passportStatusOptions, PASSPORT_STATUS_OPTIONS } from '../constants/passport-status.constants';

export type LeadDetailArea = 'platform' | 'tenant';

type AssigneeOption = {
  userId: string;
  label: string;
};

@Component({
  selector: 'app-lead-detail-page',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    RouterLink,
    AdminCardComponent,
    AdminButtonComponent,
    AdminEmptyStateComponent,
    AdminIconComponent,
    AdminSidePanelComponent,
    StatusBadgeComponent,
    WhatsappModalComponent,
    EmailModalComponent,
    LeadProfileAvatarComponent,
    LeadTimelineComponent,
    WorkflowTransitionModalComponent,
    WorkflowDocumentationModalComponent,
    LeadFormFieldsComponent,
    LeadMessagePreviewComponent,
    LeadMergeModalComponent,
    CrmTasksSectionComponent,
  ],
  template: `
    <section class="lead-detail-page">
      @if (loading()) {
        <p class="cg-form-help">Loading lead…</p>
      } @else if (!lead()) {
        <app-admin-empty-state icon="users" title="Lead not found" description="This lead may have been removed." />
        <app-admin-button variant="secondary" type="button" [routerLink]="backRoute()">Back to leads</app-admin-button>
      } @else {
        <div class="top-actions">
          <app-admin-button variant="secondary" type="button" [routerLink]="backRoute()">
            Back to list
          </app-admin-button>
        </div>

        <app-admin-card class="identity-card identity-card--hero">
          <div class="identity-card__layout">
            <app-lead-profile-avatar
              [photoUrl]="lead()!.profilePhotoUrl"
              [fullName]="lead()!.fullName"
              [uploading]="photoUploading()"
              [removing]="photoRemoving()"
              [showRemoveLink]="false"
              [showVerifiedBadge]="!!lead()!.profilePhotoUrl"
              [compact]="true"
              (photoSelected)="onPhotoSelected($event)"
              (removePhoto)="removePhoto()"
            />
            <div class="identity-card__info">
              <div class="identity-card__title-row">
                <h2 class="identity-name">{{ lead()!.fullName }}</h2>
                <app-status-badge [variant]="statusVariant(lead()!.status.code)">
                  {{ lead()!.status.name }}
                </app-status-badge>
                @if (lead()!.convertedApplicantId && lead()!.status.code === 'ENROLLED') {
                  <a class="cg-btn cg-btn-success identity-enrolled-link" [routerLink]="applicantDetailRoute()">
                    View Enrolled Applicant
                  </a>
                }
              </div>
              <div class="identity-substatus-row">
                @if (showSubStatusChange()) {
                  <label class="identity-substatus-label" for="leadSubStatus">Sub Status</label>
                  <select
                    id="leadSubStatus"
                    class="cg-select identity-substatus-select"
                    [disabled]="subStatusDropdownDisabled()"
                    [ngModel]="selectedSubStatusId"
                    (ngModelChange)="onSubStatusChange($event)"
                  >
                    @if (subStatuses().length === 0 && !lead()!.subStatusId) {
                      <option value="">No Sub Statuses Available</option>
                    } @else {
                      <option value="">Select sub status</option>
                      @for (opt of subStatuses(); track opt._id) {
                        <option [value]="opt._id">{{ opt.name }}</option>
                      }
                      @if (inactiveCurrentSubStatus(); as inactive) {
                        <option [value]="inactive._id" disabled>{{ inactive.name }} (inactive)</option>
                      }
                    }
                  </select>
                }
              </div>
              <div class="identity-contact-row">
                <span class="identity-contact-item">
                  <app-admin-icon name="mail" [size]="15" />
                  {{ lead()!.email }}
                </span>
                <span class="identity-contact-item">
                  <app-admin-icon name="phone" [size]="15" />
                  {{ lead()!.phoneNumber }}
                </span>
                @if (lead()!.preferredDestination) {
                  <span class="identity-contact-item">
                    <app-admin-icon name="map-pin" [size]="15" />
                    {{ lead()!.preferredDestination }}
                  </span>
                } @else if (lead()!.country) {
                  <span class="identity-contact-item">
                    <app-admin-icon name="map-pin" [size]="15" />
                    {{ lead()!.country }}
                  </span>
                }
              </div>
              @if (lead()!.message) {
                <app-lead-message-preview
                  variant="profile"
                  expandLabel="Show more"
                  [message]="lead()!.message!"
                  [modalTitle]="'Message — ' + lead()!.fullName"
                />
              }
            </div>
            <div class="identity-card__actions">
              @if (showEditLead()) {
                <app-admin-button variant="secondary" type="button" (click)="openEdit()" [disabled]="!canEditLead()">
                  <app-admin-icon name="pencil" [size]="16" />
                  Edit Lead
                </app-admin-button>
              }
              @if (showArchiveLead()) {
                @if (lead()!.isArchived) {
                  <app-admin-button variant="secondary" type="button" [disabled]="!canArchiveLead() || actionLoading()" (click)="restoreLead()">
                    <app-admin-icon name="rotate-ccw" [size]="16" />
                    Restore Lead
                  </app-admin-button>
                } @else {
                  <div class="identity-action identity-action--archive">
                    <app-admin-button variant="secondary" type="button" [disabled]="!canArchiveLead() || actionLoading()" (click)="archiveLead()">
                      <app-admin-icon name="archive" [size]="16" />
                      Archive Lead
                    </app-admin-button>
                  </div>
                }
              }
              @if (showSendWhatsapp()) {
                <app-admin-button variant="success" type="button" (click)="openWhatsapp()" [disabled]="!canSendWhatsapp()">
                  <app-admin-icon name="message-circle" [size]="16" />
                  Send WhatsApp
                </app-admin-button>
              }
              @if (showSendEmail()) {
                <app-admin-button variant="primary" type="button" (click)="openEmail()" [disabled]="!canSendEmail()">
                  <app-admin-icon name="send" [size]="16" />
                  Send Email
                </app-admin-button>
              }
              @if (hasDuplicateMerge() && !lead()!.isArchived && showMergeLead()) {
                <app-admin-button variant="secondary" type="button" (click)="openMergeDuplicates()" [disabled]="!canMergeLead()">
                  <app-admin-icon name="users" [size]="16" />
                  Merge Duplicate
                </app-admin-button>
              }
            </div>
          </div>
        </app-admin-card>

        <div class="detail-layout">
          <div class="detail-main">
            @if (lead()) {
              <app-admin-card>
                <h3 class="section-head">
                  <app-admin-icon name="file-text" [size]="18" />
                  Observations &amp; Notes
                </h3>
                <app-lead-timeline
                  mode="composer"
                  [leadId]="leadId()"
                  [area]="area()"
                  [currentUserId]="currentUserId()"
                  [isAdmin]="isLeadAdmin()"
                  (refreshed)="activityTimelineRef()?.reload()"
                />
              </app-admin-card>

              <app-crm-tasks-section
                #tasksSection
                entityType="lead"
                [entityId]="leadId()"
                [area]="area()"
                [isArchived]="lead()!.isArchived"
                [assignableUsers]="assignableUsers()"
                [currentUserId]="currentUserId()"
                (changed)="onTasksChanged()"
              />

              <app-admin-card>
                <div class="section-head-row">
                  <h3 class="section-head section-head--flat">
                    <app-admin-icon name="clock" [size]="18" />
                    Activity Timeline
                  </h3>
                  <button
                    type="button"
                    class="show-deleted-toggle"
                    [class.show-deleted-toggle--active]="showDeletedNotes()"
                    [attr.aria-pressed]="showDeletedNotes()"
                    (click)="toggleShowDeletedNotes()"
                  >
                    {{ showDeletedNotes() ? 'Back to timeline' : 'Show deleted notes' }}
                  </button>
                </div>
                <app-lead-timeline
                  #activityTimeline
                  mode="activity"
                  [leadId]="leadId()"
                  [area]="area()"
                  [includeArchivedNotes]="showDeletedNotes()"
                  [currentUserId]="currentUserId()"
                  [isAdmin]="isLeadAdmin()"
                  (noteRestored)="onNoteRestored()"
                />
              </app-admin-card>
            }

            <app-admin-card class="panel-card">
              <button
                type="button"
                class="panel-head panel-head--collapsible"
                [class.panel-head--expanded]="communicationsExpanded()"
                [attr.aria-expanded]="communicationsExpanded()"
                aria-controls="lead-comms-panel"
                (click)="toggleCommunicationsPanel()"
              >
                <span class="section-head section-head--inline">
                  <app-admin-icon name="message-square" [size]="18" />
                  Recent Communications
                </span>
                @if (communicationsCount() > 0) {
                  <span class="panel-count">{{ communicationsCount() }}</span>
                }
                <app-admin-icon
                  [name]="communicationsExpanded() ? 'chevron-down' : 'chevron-right'"
                  [size]="18"
                />
              </button>
              @if (communicationsExpanded()) {
                <div id="lead-comms-panel" class="panel-body">
                  @if (communicationsLoading()) {
                    <p class="cg-form-help panel-loading">Loading communications…</p>
                  } @else if (communications().length === 0) {
                    <app-admin-empty-state
                      icon="message-square-quote"
                      title="No communications yet"
                      description="Send a WhatsApp message or email to start the timeline."
                    />
                  } @else {
                    <ul class="comm-timeline">
                      @for (log of communications(); track log.id) {
                        <li class="comm-item">
                          <div class="comm-item__head">
                            <div class="comm-item__head-left">
                              <span class="comm-channel" [class]="communicationChannelClass(log)">
                                {{ communicationChannelLabel(log) }}
                              </span>
                              <time>{{ log.createdAt | date: 'medium' }}</time>
                            </div>
                            <span class="comm-status" [class]="communicationStatusClass(log.status)">
                              {{ communicationStatusLabel(log.status) }}
                            </span>
                          </div>
                          @if (isEmail(log) && log.subject) {
                            <p class="comm-item__subject">{{ log.subject }}</p>
                          }
                          @if (isEmail(log)) {
                            @if (log.to?.length) {
                              <p class="comm-item__meta"><span>To</span> {{ log.to!.join(', ') }}</p>
                            }
                            @if (log.cc?.length) {
                              <p class="comm-item__meta"><span>Cc</span> {{ log.cc!.join(', ') }}</p>
                            }
                            @if (log.bcc?.length) {
                              <p class="comm-item__meta"><span>Bcc</span> {{ log.bcc!.join(', ') }}</p>
                            }
                          }
                          <p class="comm-item__preview">{{ messagePreview(log.message) }}</p>
                        </li>
                      }
                    </ul>

                    @if (communicationsHasMore()) {
                      <div class="comm-footer">
                        <app-admin-button
                          variant="secondary"
                          type="button"
                          [disabled]="communicationsLoadingMore()"
                          (click)="loadMoreCommunications()"
                        >
                          {{ communicationsLoadingMore() ? 'Loading…' : 'Load more' }}
                        </app-admin-button>
                      </div>
                    }
                  }
                </div>
              }
            </app-admin-card>
          </div>

          <aside class="detail-aside">
            <app-admin-card>
              <h3 class="section-head">
                <app-admin-icon name="contact-round" [size]="18" />
                Contact Details
              </h3>
              <div class="aside-fields">
                <div class="aside-field">
                  <span class="aside-field__label">Email address</span>
                  <p class="aside-field__value">{{ lead()!.email }}</p>
                </div>
                <div class="aside-field">
                  <span class="aside-field__label">Phone number</span>
                  <p class="aside-field__value">{{ lead()!.phoneNumber }}</p>
                </div>
                <div class="aside-field">
                  @if (lead()!.isArchived) {
                    <span class="aside-field__label">Lead source</span>
                    <span class="source-pill">{{ displayLeadSource() }}</span>
                  } @else {
                    <label class="cg-admin-field">
                      <span class="cg-admin-field__label">Lead source</span>
                      <select
                        class="cg-select"
                        [(ngModel)]="selectedLeadSource"
                        (ngModelChange)="onLeadSourceChange($event)"
                        name="leadSource"
                        [disabled]="leadSourceSaving()"
                      >
                        @for (source of leadSourceOptions; track source) {
                          <option [value]="source">{{ source }}</option>
                        }
                      </select>
                    </label>
                    @if (leadSourceSaving()) {
                      <p class="cg-form-help">Updating lead source…</p>
                    }
                  }
                </div>
                @if (lead()!.sourcePage) {
                  <div class="aside-field">
                    <span class="aside-field__label">Source page</span>
                    <span class="source-pill">{{ formatSourceLabel(lead()!.sourcePage!) }}</span>
                  </div>
                }
                @if (lead()!.stream) {
                  <div class="aside-field">
                    <span class="aside-field__label">Stream</span>
                    <p class="aside-field__value">{{ lead()!.stream }}</p>
                  </div>
                }
                @if (lead()!.section) {
                  <div class="aside-field">
                    <span class="aside-field__label">Section</span>
                    <p class="aside-field__value">{{ lead()!.section }}</p>
                  </div>
                }
              </div>
            </app-admin-card>

            <app-admin-card>
              <h3 class="section-head">
                <app-admin-icon name="circle-user" [size]="18" />
                Ownership
              </h3>
              <div class="aside-fields">
                <div class="aside-field">
                  <span class="aside-field__label">Assigned to</span>
                  <p class="aside-field__value">{{ lead()!.owner?.displayName || 'Unassigned' }}</p>
                </div>
                @if (!lead()!.isArchived && showAssignLead()) {
                  <label class="cg-admin-field">
                    <span class="cg-admin-field__label">Change owner</span>
                    <select
                      class="cg-select"
                      [(ngModel)]="selectedOwnerUserId"
                      (ngModelChange)="onOwnerSelectionChange($event)"
                      name="ownerUserId"
                      [disabled]="!canAssignLead() || assignSaving() || assignableUsers().length === 0"
                    >
                      <option value="">Select a user…</option>
                      @for (user of assignableUsers(); track user.userId) {
                        <option [value]="user.userId">{{ user.label }}</option>
                      }
                    </select>
                  </label>
                  @if (assignSaving()) {
                    <p class="cg-form-help">Updating owner…</p>
                  }
                } @else if (lead()!.isArchived) {
                  <p class="cg-form-help">Ownership cannot be changed while this lead is archived.</p>
                }
              </div>
            </app-admin-card>

            <app-admin-card>
              <div class="section-head-row">
                <h3 class="section-head section-head--flat">
                  <app-admin-icon name="clipboard-check" [size]="18" />
                  Workflow Actions
                </h3>
                <app-admin-button
                  variant="secondary"
                  type="button"
                  class="view-workflow-btn"
                  (click)="openWorkflowDocumentation()"
                >
                  <app-admin-icon name="eye" [size]="16" />
                  View Workflow
                </app-admin-button>
              </div>
              <div class="workflow-actions-body">
                @if (lead()!.isArchived) {
                  <p class="cg-form-help">Transitions are disabled while this lead is archived.</p>
                } @else if (!showStatusChangeLead()) {
                  <p class="cg-form-help">Status transitions are not available for your role.</p>
                } @else if (transitionsLoading()) {
                  <p class="cg-form-help">Loading transitions…</p>
                } @else if (transitions().length === 0) {
                  <p class="cg-form-help">No transitions available from the current status.</p>
                } @else {
                  <div class="transition-actions">
                    @for (t of transitions(); track t._id) {
                      <div
                        class="transition-action"
                        [class.transition-action--reject]="isRejectTransition(t)"
                      >
                        <app-admin-button
                          [variant]="isCreateApplicantTransition(t) ? 'success' : 'secondary'"
                          type="button"
                          [disabled]="!canStatusChangeLead() || actionLoading()"
                          (click)="openTransitionModal(t)"
                        >
                          <app-admin-icon [name]="transitionIcon(t)" [size]="16" />
                          {{ transitionLabel(t) }}
                        </app-admin-button>
                      </div>
                    }
                  </div>
                }
              </div>
            </app-admin-card>

            <app-admin-card>
              <h3 class="section-head">
                <app-admin-icon name="globe" [size]="18" />
                Preferences
              </h3>
              @if (lead()!.preferredDestination) {
                <div class="pref-destination">
                  <span class="pref-destination__icon" aria-hidden="true">
                    <app-admin-icon name="plane" [size]="18" />
                  </span>
                  <div>
                    <span class="pref-destination__label">Destination</span>
                    <strong class="pref-destination__value">{{ lead()!.preferredDestination }}</strong>
                  </div>
                </div>
              }
              <div class="pref-state">
                <span class="pref-state__label">Lead State</span>
                @if (lead()!.isArchived) {
                  <span class="pref-state__value pref-state__value--archived">
                    <span class="pref-state__dot pref-state__dot--archived"></span>
                    Archived
                  </span>
                  @if (lead()!.archivedAt) {
                    <span class="pref-state__meta">{{ lead()!.archivedAt | date: 'medium' }}</span>
                  }
                } @else {
                  <span class="pref-state__value">
                    <span class="pref-state__dot"></span>
                    Currently Active
                  </span>
                }
              </div>
            </app-admin-card>
          </aside>
        </div>
      }

      <app-workflow-transition-modal
        [open]="transitionModalOpen()"
        [transition]="pendingTransition()"
        [currentStatus]="lead()?.status ?? null"
        [busy]="actionLoading()"
        [error]="transitionError()"
        (confirmed)="applyTransition($event.comment)"
        (closed)="closeTransitionModal()"
      />

      <app-workflow-documentation-modal
        [open]="workflowDocOpen()"
        [loading]="workflowDocLoading()"
        [error]="workflowDocError()"
        [statuses]="workflowDocStatuses()"
        [steps]="workflowDocSteps()"
        [currentStatus]="lead()?.status ?? null"
        [availableTransitions]="transitions()"
        (closed)="closeWorkflowDocumentation()"
      />

      @if (editOpen()) {
        <app-admin-side-panel title="Edit lead" (closed)="closeEdit()">
          <div class="edit-profile-header">
            <app-lead-profile-avatar
              [photoUrl]="lead()!.profilePhotoUrl"
              [fullName]="editForm.fullName || lead()!.fullName"
              [uploading]="photoUploading()"
              [removing]="photoRemoving()"
              hint="Passport photo · 3″ × 4″ · max 5 MB"
              (photoSelected)="onPhotoSelected($event)"
              (removePhoto)="removePhoto()"
            />
            @if (photoError()) {
              <p class="edit-profile-header__error">{{ photoError() }}</p>
            }
          </div>

          <app-lead-form-fields
            [form]="editForm"
            [fieldErrors]="editFieldErrors()"
            [destinationOptions]="destinationOptions()"
            [stateOptions]="stateOptions()"
            [passportStatusOptions]="passportStatusOptionList()"
          />
          @if (editError()) {
            <p class="mt-3 text-sm font-medium text-red-600">{{ editError() }}</p>
          }
          <div class="edit-panel-footer">
            <app-admin-button variant="primary" type="button" [disabled]="editSaving()" (click)="saveEdit()">
              {{ editSaving() ? 'Saving…' : 'Save changes' }}
            </app-admin-button>
            <app-admin-button variant="secondary" type="button" (click)="closeEdit()">Cancel</app-admin-button>
          </div>
        </app-admin-side-panel>
      }

      <app-whatsapp-modal
        [open]="whatsappOpen()"
        [leadId]="leadId()"
        [defaultPhone]="defaultPhone()"
        (closed)="closeWhatsapp()"
        (sent)="onWhatsappSent()"
      />

      <app-email-modal
        [open]="emailOpen()"
        [leadId]="leadId()"
        [defaultEmail]="defaultEmail()"
        (closed)="closeEmail()"
        (sent)="onEmailSent()"
      />

      @if (showMergeLead() && mergeOpen() && mergeLeadIds().length >= 2) {
        <app-lead-merge-modal
          [area]="area()"
          [leadIds]="mergeLeadIds()"
          (closed)="closeMergeDuplicates()"
          (merged)="onMergeDuplicatesCompleted($event)"
        />
      }
    </section>
  `,
  styles: `
    .lead-detail-page { display: flex; flex-direction: column; gap: 0.875rem; }
    .top-actions { display: flex; align-items: center; }
    app-admin-card.identity-card--hero {
      display: block;
    }
    app-admin-card.identity-card--hero ::ng-deep .cg-admin-card__body {
      padding: 1rem 1.25rem;
    }
    .detail-layout {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(16rem, var(--cg-admin-aside-width, 22rem));
      gap: 1rem;
      align-items: start;
      width: 100%;
      max-width: 100%;
      min-width: 0;
    }
    .detail-main,
    .detail-aside {
      display: flex;
      flex-direction: column;
      gap: 0.875rem;
      min-width: 0;
    }
    .section-head {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin: 0 0 0.875rem;
      font-size: 0.9375rem;
      font-weight: 600;
      color: #0f172a;
    }
    .section-head--flat { margin: 0; }
    .section-head-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      margin-bottom: 0.875rem;
      flex-wrap: wrap;
    }
    .section-head-row .section-head {
      flex: 1 1 auto;
      min-width: 0;
    }
    .filter-logs-btn {
      border: none;
      background: none;
      padding: 0;
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--cg-color-primary, #072b61);
      cursor: not-allowed;
      opacity: 0.55;
    }
    .show-deleted-toggle {
      border: 1px solid #cbd5e1;
      background: #fff;
      border-radius: 0.375rem;
      padding: 0.35rem 0.65rem;
      font-size: 0.75rem;
      font-weight: 600;
      color: #475569;
      cursor: pointer;
      transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    }
    .show-deleted-toggle:hover {
      border-color: #94a3b8;
      color: #0f172a;
    }
    .show-deleted-toggle--active {
      border-color: var(--cg-color-primary, #072b61);
      background: color-mix(in srgb, var(--cg-color-primary, #072b61) 8%, white);
      color: var(--cg-color-primary, #072b61);
    }
    .section-head app-admin-icon { color: var(--cg-color-primary, #072b61); flex-shrink: 0; }
    .section-head--inline { margin: 0; flex: 1; min-width: 0; }
    .detail-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(12rem, 100%), 1fr)); gap: .65rem .85rem; }
    .detail-grid p, .detail-inline-label { margin: 0; font-size: .9rem; color: #334155; }
    .detail-grid span, .detail-inline-label { display: block; font-size: .72rem; text-transform: uppercase; color: #94a3b8; margin-bottom: .15rem; }
    .detail-inline-label { display: inline; margin-right: .35rem; }
    .meta-label { margin: 0 0 .35rem; text-transform: uppercase; letter-spacing: .05em; font-size: .75rem; color: #6b7280; }
    .identity-card__layout {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      gap: 1rem;
      align-items: start;
      width: 100%;
      max-width: 100%;
      min-width: 0;
    }
    .identity-card__title-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.65rem;
      margin-bottom: 0.5rem;
    }
    .identity-enrolled-link {
      padding: 0.35rem 0.75rem;
      font-size: 0.8125rem;
      text-decoration: none;
      line-height: 1.2;
    }
    .identity-name { margin: 0; font-size: 1.25rem; font-weight: 700; color: #0f172a; line-height: 1.2; }

    .identity-substatus-row {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-1);
      margin-top: var(--cg-space-2);
      max-width: 16rem;
    }

    .identity-substatus-label {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--cg-color-text-muted, #64748b);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .identity-substatus-select {
      width: 100%;
      min-width: 0;
    }
    .identity-contact-row {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem 1.125rem;
    }
    .identity-contact-item {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.875rem;
      color: #475569;
    }
    .identity-contact-item app-admin-icon { color: #94a3b8; flex-shrink: 0; }
    .identity-card__actions {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      gap: 0.45rem;
      min-width: 0;
      max-width: 100%;
    }
    .identity-card__actions ::ng-deep .cg-admin-button-host {
      display: flex;
      width: 100%;
    }
    .identity-card__actions ::ng-deep .cg-btn {
      width: 100%;
      justify-content: center;
      padding: 0.55rem 0.85rem;
      font-size: 0.8125rem;
    }
    .transition-action {
      flex: 0 1 auto;
      min-width: 0;
    }
    .transition-action ::ng-deep .cg-admin-button-host {
      display: inline-flex;
      width: auto;
    }
    .transition-action ::ng-deep .cg-btn {
      width: auto;
      max-width: 100%;
      justify-content: center;
      padding: 0.55rem 0.85rem;
      font-size: 0.8125rem;
      white-space: normal;
      text-align: center;
    }
    .identity-action--archive ::ng-deep .cg-btn {
      background: transparent;
      border-color: var(--cg-color-danger, #dc2626);
      color: var(--cg-color-danger, #dc2626);
    }
    .identity-action--archive ::ng-deep .cg-btn:hover:not(:disabled) {
      background: #fef2f2;
      border-color: var(--cg-color-danger, #dc2626);
      color: var(--cg-color-danger, #dc2626);
    }
    .transition-action--reject ::ng-deep .cg-btn {
      color: var(--cg-color-danger, #dc2626);
      border-color: #fecaca;
    }
    .transition-action--reject ::ng-deep .cg-btn:hover:not(:disabled) {
      background: #fef2f2;
      border-color: #fca5a5;
      color: var(--cg-color-danger, #dc2626);
    }
    .aside-fields { display: flex; flex-direction: column; gap: 0.85rem; }
    .aside-field { display: flex; flex-direction: column; gap: 0.2rem; }
    .aside-field__label {
      font-size: 0.6875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #94a3b8;
    }
    .aside-field__value {
      display: block;
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: #0f172a;
      word-break: break-word;
      line-height: 1.4;
    }
    .aside-field__value--muted { font-weight: 400; color: #475569; line-height: 1.45; }
    .source-pill {
      display: inline-flex;
      align-self: flex-start;
      padding: 0.15rem 0.55rem;
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 600;
      background: #f1f5f9;
      color: #475569;
      border: 1px solid #e2e8f0;
    }
    .transition-actions {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: stretch;
      gap: 0.55rem;
    }
    .workflow-actions-body {
      padding: var(--cg-space-4);
      border: 1px solid var(--cg-color-border);
      border-radius: var(--cg-radius-lg);
      background: color-mix(in srgb, var(--cg-color-bg) 88%, var(--cg-color-card));
    }
    .view-workflow-btn {
      flex: 0 0 auto;
      width: auto;
      margin-left: auto;
    }
    .view-workflow-btn ::ng-deep .cg-btn {
      width: auto;
      white-space: nowrap;
      padding: 0.45rem 0.75rem;
      font-size: 0.8125rem;
    }
    @media (max-width: 420px) {
      .section-head-row {
        flex-wrap: wrap;
      }
      .view-workflow-btn {
        margin-left: 0;
      }
    }
    .pref-destination {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem;
      margin-bottom: 0.75rem;
      border: 1px solid #e2e8f0;
      border-radius: 0.5rem;
      background: #f8fafc;
    }
    .pref-destination__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: 0.375rem;
      background: color-mix(in srgb, var(--cg-color-primary, #2563eb) 10%, #fff);
      color: var(--cg-color-primary, #2563eb);
    }
    .pref-destination__label {
      display: block;
      font-size: 0.6875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #94a3b8;
      margin-bottom: 0.1rem;
    }
    .pref-destination__value { font-size: 0.9375rem; color: #0f172a; }
    .pref-state { display: flex; flex-direction: column; gap: 0.25rem; }
    .pref-state__label {
      font-size: 0.6875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #94a3b8;
    }
    .pref-state__value {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      font-size: 0.9375rem;
      font-weight: 600;
      color: #0f172a;
    }
    .pref-state__value--archived { color: #64748b; }
    .pref-state__dot {
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 999px;
      background: #22c55e;
      flex-shrink: 0;
    }
    .pref-state__dot--archived { background: #94a3b8; }
    .pref-state__meta { font-size: 0.8125rem; color: #94a3b8; }
    .edit-profile-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 1.25rem;
      padding-bottom: 1.25rem;
      border-bottom: 1px solid #e2e8f0;
    }
    .edit-profile-header__error {
      margin: 0.5rem 0 0;
      font-size: 0.75rem;
      color: #b91c1c;
      text-align: center;
    }
    .edit-panel-footer {
      display: flex;
      flex-wrap: wrap;
      gap: .75rem;
      margin-top: 1.25rem;
      padding-top: 1rem;
      border-top: 1px solid #e2e8f0;
    }
    app-admin-card.panel-card {
      display: block;
    }
    app-admin-card.panel-card ::ng-deep .cg-admin-card__body {
      padding: 0;
    }
    .panel-head {
      padding: 1rem 1.25rem;
      border-bottom: 1px solid #e2e8f0;
    }
    .panel-head--collapsible {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      margin: 0;
      border: none;
      border-bottom: none;
      background: #fff;
      cursor: pointer;
      text-align: left;
      transition: background 0.15s ease;
    }
    .panel-head--collapsible.panel-head--expanded {
      border-bottom: 1px solid #e2e8f0;
    }
    .panel-head--collapsible:hover { background: #f8fafc; }
    .panel-count {
      font-size: 0.75rem;
      font-weight: 600;
      color: #64748b;
      background: #f1f5f9;
      border-radius: 999px;
      padding: 0.1rem 0.5rem;
      line-height: 1.4;
    }
    .panel-body { padding: 0 1.25rem 1rem; }
    .panel-loading { padding: 1rem 1.25rem; }
    .comm-timeline { list-style: none; margin: 0; padding: 0; }
    .comm-item { padding: 1rem 0; border-bottom: 1px solid #f1f5f9; }
    .comm-item:last-child { border-bottom: none; }
    .comm-footer { padding: 0.9rem 0; display: flex; justify-content: center; border-top: 1px solid #f1f5f9; }
    .comm-item__head { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: .5rem; margin-bottom: .35rem; }
    .comm-item__head-left { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem; }
    .comm-item__head time { font-size: .85rem; color: #64748b; }
    .comm-item__subject { margin: 0 0 .25rem; font-weight: 650; color: #0f172a; }
    .comm-item__meta {
      margin: 0 0 .2rem;
      color: #64748b;
      font-size: .82rem;
      line-height: 1.4;
      word-break: break-word;
    }
    .comm-item__meta span {
      font-weight: 600;
      color: #475569;
      margin-right: .35rem;
    }
    .comm-item__preview { margin: 0; color: #334155; font-size: .95rem; line-height: 1.45; }
    .comm-channel {
      display: inline-flex; align-items: center; padding: 0.18rem 0.5rem; border-radius: 999px;
      font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
      border: 1px solid #e2e8f0; background: #f8fafc; color: #334155;
    }
    .comm-channel--whatsapp { border-color: #bbf7d0; background: #ecfdf5; color: #047857; }
    .comm-channel--email { border-color: #bfdbfe; background: #eff6ff; color: #1d4ed8; }
    .comm-status {
      display: inline-block; padding: 0.2rem 0.55rem; border-radius: 999px;
      font-size: 0.72rem; font-weight: 600; text-transform: capitalize; letter-spacing: 0.02em;
    }
    .comm-status--queued { background: #e5e7eb; color: #4b5563; }
    .comm-status--sent { background: #fef3c7; color: #92400e; }
    .comm-status--delivered { background: #dbeafe; color: #1d4ed8; }
    .comm-status--read { background: #dcfce7; color: #15803d; }
    .comm-status--failed { background: #fee2e2; color: #b91c1c; }
    @media (max-width: 1439px) {
      .detail-layout {
        grid-template-columns: minmax(0, 1fr) minmax(14rem, 18rem);
      }
    }
    @media (max-width: 1279px) {
      .detail-layout { grid-template-columns: 1fr; }
      .detail-aside { order: -1; }
      .identity-card__layout {
        grid-template-columns: 1fr;
        justify-items: center;
        text-align: center;
        gap: 1.15rem;
      }
      .identity-card__info {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
      }
      .identity-card__title-row {
        justify-content: center;
        margin-bottom: 0;
      }
      .identity-substatus-row {
        width: min(100%, 18rem);
        max-width: 18rem;
        margin-inline: auto;
        text-align: left;
      }
      .identity-substatus-label {
        text-align: center;
      }
      .identity-contact-row {
        justify-content: center;
        width: 100%;
      }
      .identity-card__info app-lead-message-preview {
        display: block;
        width: min(100%, 28rem);
        margin-inline: auto;
        text-align: center;
      }
      .identity-card__actions {
        grid-column: 1 / -1;
        width: min(100%, 28rem);
        margin-inline: auto;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.55rem;
        min-width: 0;
      }
      .identity-card__actions > *,
      .identity-card__actions .identity-action {
        flex: 1 1 calc(50% - 0.3rem);
        min-width: min(100%, 9.5rem);
        max-width: 100%;
      }
      .identity-card__actions .identity-action {
        display: flex;
      }
      .identity-card__actions ::ng-deep .cg-admin-button-host,
      .identity-card__actions ::ng-deep .cg-btn {
        width: 100%;
      }
    }
    @media (max-width: 1024px) {
      .identity-card__layout {
        grid-template-columns: 1fr;
        justify-items: center;
        text-align: center;
      }
    }
    @media (max-width: 639px) {
      .identity-card__actions {
        width: 100%;
      }
      .identity-card__actions > *,
      .identity-card__actions .identity-action {
        flex: 1 1 100%;
      }
    }
  `,
})
export class LeadDetailPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly leadsService = inject(LeadsService);
  private readonly tenantLeadsService = inject(TenantLeadsService);
  private readonly communicationsService = inject(CommunicationsService);
  private readonly countriesService = inject(CountriesService);
  private readonly toast = inject(AdminToastService);
  private readonly authService = inject(AuthService);
  private readonly permissionService = inject(PermissionService);
  private readonly platformUsersService = inject(PlatformUsersService);
  private readonly tenantUsersService = inject(TenantUsersService);
  readonly activityTimelineRef = viewChild<LeadTimelineComponent>('activityTimeline');
  readonly tasksSectionRef = viewChild<CrmTasksSectionComponent>('tasksSection');

  readonly area = computed<LeadDetailArea>(() =>
    this.route.snapshot.data['leadArea'] === 'tenant' ? 'tenant' : 'platform',
  );

  readonly leadId = signal('');
  readonly lead = signal<LeadDto | null>(null);
  readonly loading = signal(true);
  readonly actionLoading = signal(false);
  readonly transitions = signal<LeadTransitionOption[]>([]);
  readonly transitionsLoading = signal(false);
  readonly transitionModalOpen = signal(false);
  readonly pendingTransition = signal<LeadTransitionOption | null>(null);
  readonly transitionError = signal<string | null>(null);
  readonly workflowDocOpen = signal(false);
  readonly workflowDocLoading = signal(false);
  readonly workflowDocError = signal<string | null>(null);
  readonly workflowDocSteps = signal<WorkflowDocumentationStep[]>([]);
  readonly workflowDocStatuses = signal<WorkflowDocumentationStatus[]>([]);
  readonly subStatuses = signal<LeadSubStatusOption[]>([]);
  readonly subStatusesLoading = signal(false);
  readonly subStatusSaving = signal(false);
  selectedSubStatusId = '';
  readonly editOpen = signal(false);
  readonly editSaving = signal(false);
  readonly editError = signal<string | null>(null);
  readonly editFieldErrors = signal<LeadFormFieldErrors>({});
  editForm: LeadFormFields = {
    fullName: '',
    email: '',
    phoneNumber: '',
    whatsappNumber: '',
    city: '',
    state: '',
    country: '',
    qualification: '',
    percentageOrCgpa: '',
    passingYear: '',
    institution: '',
    preferredDestination: '',
    preferredCourse: '',
    intake: '',
    budgetRange: '',
    passportStatus: '',
    englishTestStatus: '',
    loanRequired: '',
    stream: '',
    section: '',
    leadSource: 'Website',
    message: '',
  };

  readonly communications = signal<CommunicationDto[]>([]);
  readonly communicationsLoading = signal(false);
  readonly communicationsLoadingMore = signal(false);
  readonly communicationsPage = signal(1);
  readonly communicationsHasMore = signal(false);
  readonly communicationsExpanded = signal(false);
  readonly showDeletedNotes = signal(false);
  readonly whatsappOpen = signal(false);
  readonly emailOpen = signal(false);
  readonly mergeOpen = signal(false);
  readonly mergeLeadIds = signal<string[]>([]);
  readonly hasDuplicateMerge = signal(false);
  readonly destinationOptions = signal<string[]>([]);
  readonly stateOptions = signal<string[]>([...INDIAN_STATES]);
  readonly passportStatusOptionList = signal<string[]>([...PASSPORT_STATUS_OPTIONS]);
  readonly photoUploading = signal(false);
  readonly photoRemoving = signal(false);
  readonly photoError = signal<string | null>(null);
  readonly assignableUsers = signal<AssigneeOption[]>([]);
  readonly assignSaving = signal(false);
  readonly leadSourceSaving = signal(false);
  readonly leadSourceOptions = LEAD_SOURCE_OPTIONS;
  selectedOwnerUserId = '';
  selectedLeadSource: LeadSourceOption = 'Website';

  readonly currentUserId = computed(() => {
    if (this.area() === 'tenant') {
      return this.authService.getTenantUser()?.userId ?? null;
    }
    return this.authService.getCurrentUser()?.userId ?? null;
  });

  readonly isLeadAdmin = computed(() => {
    if (this.area() === 'tenant') {
      return this.authService.getTenantUser()?.role === 'FRANCHISE_ADMIN';
    }
    const roleKey = this.authService.getCurrentUser()?.roleKey;
    return roleKey === 'SUPER_ADMIN' || roleKey === 'PLATFORM_STAFF';
  });

  readonly backRoute = computed(() =>
    this.area() === 'tenant' ? '/tenant/leads' : '/platform/leads',
  );

  readonly applicantDetailRoute = computed(() => {
    const applicantId = this.lead()?.convertedApplicantId;
    if (!applicantId) return this.backRoute();
    return this.area() === 'tenant'
      ? ['/tenant/applicants', applicantId]
      : ['/platform/applicants', applicantId];
  });

  readonly defaultPhone = computed(() => {
    const raw = this.lead()?.phoneNumber ?? '';
    return raw.replace(/\D/g, '');
  });

  readonly defaultEmail = computed(() => this.lead()?.email ?? '');

  readonly communicationsCount = computed(() => this.communications().length);

  readonly inactiveCurrentSubStatus = computed(() => {
    const lead = this.lead();
    if (!lead?.subStatusId || !lead.subStatus) return null;
    const inList = this.subStatuses().some((row) => row._id === lead.subStatusId);
    return inList ? null : { _id: lead.subStatusId, name: lead.subStatus.name };
  });

  /** Platform action levels; tenant area keeps full UI (tenant RBAC separate). */
  private leadActionLevel(actionKey: string) {
    this.authService.currentUser();
    if (this.area() === 'tenant') return 'EXECUTE' as const;
    return this.permissionService.getActionLevel('leads', actionKey);
  }

  readonly showSubStatusChange = computed(
    () => this.leadActionLevel('subStatusChange') !== 'NO_ACCESS',
  );
  readonly canChangeSubStatus = computed(
    () => this.leadActionLevel('subStatusChange') === 'EXECUTE',
  );
  readonly showEditLead = computed(() => this.leadActionLevel('edit') !== 'NO_ACCESS');
  readonly canEditLead = computed(() => this.leadActionLevel('edit') === 'EXECUTE');
  readonly showArchiveLead = computed(() => this.leadActionLevel('archive') !== 'NO_ACCESS');
  readonly canArchiveLead = computed(() => this.leadActionLevel('archive') === 'EXECUTE');
  readonly showSendWhatsapp = computed(
    () => this.leadActionLevel('sendWhatsapp') !== 'NO_ACCESS',
  );
  readonly canSendWhatsapp = computed(
    () => this.leadActionLevel('sendWhatsapp') === 'EXECUTE',
  );
  readonly showSendEmail = computed(() => this.leadActionLevel('sendEmail') !== 'NO_ACCESS');
  readonly canSendEmail = computed(() => this.leadActionLevel('sendEmail') === 'EXECUTE');
  readonly showAssignLead = computed(() => this.leadActionLevel('assign') !== 'NO_ACCESS');
  readonly canAssignLead = computed(() => this.leadActionLevel('assign') === 'EXECUTE');
  readonly showStatusChangeLead = computed(() => this.leadActionLevel('statusChange') !== 'NO_ACCESS');
  readonly canStatusChangeLead = computed(() => this.leadActionLevel('statusChange') === 'EXECUTE');
  readonly showMergeLead = computed(() => this.leadActionLevel('merge') !== 'NO_ACCESS');
  readonly canMergeLead = computed(() => this.leadActionLevel('merge') === 'EXECUTE');

  readonly subStatusDropdownDisabled = computed(() => {
    const lead = this.lead();
    if (!lead || lead.isArchived || this.subStatusSaving() || this.subStatusesLoading()) {
      return true;
    }
    if (this.subStatuses().length === 0 && !lead.subStatusId) {
      return true;
    }
    if (!this.canChangeSubStatus()) {
      return true;
    }
    return false;
  });

  private communicationsLoaded = false;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    this.leadId.set(id);
    this.loadAssignableUsers();
    this.loadLead(id);
    this.openTaskFromQuery();
  }

  private openTaskFromQuery(): void {
    const taskId = this.route.snapshot.queryParamMap.get('taskId');
    if (!taskId) return;

    setTimeout(() => {
      this.tasksSectionRef()?.openTaskById(taskId);
    }, 0);
  }

  displayLeadSource(): string {
    return resolveLeadSource(this.lead()?.leadSource);
  }

  onLeadSourceChange(leadSource: string): void {
    const next = resolveLeadSource(leadSource);
    const current = resolveLeadSource(this.lead()?.leadSource);

    if (next === current) {
      return;
    }

    this.saveLeadSource(next);
  }

  private saveLeadSource(leadSource: LeadSourceOption): void {
    const id = this.leadId();
    if (!id) return;

    this.leadSourceSaving.set(true);

    const req =
      this.area() === 'tenant'
        ? this.tenantLeadsService.update(id, { leadSource })
        : this.leadsService.update(id, { leadSource });

    req.subscribe({
      next: (updated) => {
        this.lead.set(updated);
        this.selectedLeadSource = resolveLeadSource(updated.leadSource);
        this.leadSourceSaving.set(false);
        this.refreshTimeline();
      },
      error: (err) => {
        this.leadSourceSaving.set(false);
        this.selectedLeadSource = resolveLeadSource(this.lead()?.leadSource);
        this.toast.error(err?.error?.message ?? 'Could not update lead source.');
      },
    });
  }

  onOwnerSelectionChange(ownerUserId: string): void {
    const trimmed = ownerUserId.trim();
    const currentOwnerId = this.lead()?.ownerUserId ?? '';

    if (!trimmed) {
      this.selectedOwnerUserId = currentOwnerId;
      return;
    }

    if (trimmed === currentOwnerId) {
      return;
    }

    this.saveOwnerAssignment(trimmed);
  }

  private saveOwnerAssignment(ownerUserId: string): void {
    const id = this.leadId();
    if (!id || !ownerUserId.trim()) return;

    this.assignSaving.set(true);

    const req =
      this.area() === 'tenant'
        ? this.tenantLeadsService.assignOwner(id, ownerUserId)
        : this.leadsService.assignOwner(id, ownerUserId);

    req.subscribe({
      next: (updated) => {
        this.lead.set(updated);
        this.selectedOwnerUserId = updated.ownerUserId ?? '';
        this.assignSaving.set(false);
        const name = updated.owner?.displayName ?? 'selected user';
        this.toast.success(`Lead assigned to ${name}.`);
        this.refreshTimeline();
      },
      error: (err) => {
        this.assignSaving.set(false);
        this.selectedOwnerUserId = this.lead()?.ownerUserId ?? '';
        this.toast.error(err?.error?.message ?? 'Could not update owner.');
      },
    });
  }

  private loadAssignableUsers(): void {
    if (this.area() === 'tenant') {
      this.tenantUsersService.list().subscribe({
        next: (users) => {
          this.assignableUsers.set(
            users
              .filter((u) => u.isActive)
              .map((u) => ({
                userId: u.userId,
                label: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email,
              }))
              .sort((a, b) => a.label.localeCompare(b.label)),
          );
        },
        error: () => this.assignableUsers.set([]),
      });
      return;
    }

    this.platformUsersService.listAssignable().subscribe({
      next: (users) => {
        this.assignableUsers.set(
          users
            .map((u) => ({ userId: u.userId, label: u.name }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        );
      },
      error: () => this.assignableUsers.set([]),
    });
  }

  toggleCommunicationsPanel(): void {
    const next = !this.communicationsExpanded();
    this.communicationsExpanded.set(next);
    if (next && !this.communicationsLoaded) {
      this.refreshCommunications();
    }
  }

  toggleShowDeletedNotes(): void {
    this.showDeletedNotes.update((value) => !value);
  }

  onNoteRestored(): void {
    this.showDeletedNotes.set(false);
  }

  hasLocationFields(): boolean {
    const l = this.lead();
    return Boolean(l?.city || l?.state || l?.country);
  }

  hasAcademicFields(): boolean {
    const l = this.lead();
    return Boolean(l?.qualification || l?.percentageOrCgpa || l?.passingYear || l?.institution);
  }

  hasStudyAbroadFields(): boolean {
    const l = this.lead();
    return Boolean(
      l?.preferredDestination ||
        l?.preferredCourse ||
        l?.intake ||
        l?.budgetRange ||
        l?.passportStatus ||
        l?.englishTestStatus ||
        l?.loanRequired,
    );
  }

  openEdit(): void {
    const current = this.lead();
    if (!current) return;
    this.editForm = leadFormFromLead(current);
    this.editError.set(null);
    this.editFieldErrors.set({});
    this.photoError.set(null);
    this.stateOptions.set(indianStateOptions(current.state));
    this.passportStatusOptionList.set(passportStatusOptions(current.passportStatus));
    this.syncDestinationOptions(current.preferredDestination);
    this.editOpen.set(true);
  }

  private syncDestinationOptions(currentValue?: string): void {
    const pref = currentValue?.trim();
    this.countriesService.listPublicCountries().subscribe({
      next: (list) => {
        const names = list.map((c) => c.name?.trim()).filter((v): v is string => Boolean(v));
        const sorted = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
        if (pref && !sorted.includes(pref)) {
          this.destinationOptions.set([pref, ...sorted]);
        } else {
          this.destinationOptions.set(sorted);
        }
      },
      error: () => {
        this.destinationOptions.set(pref ? [pref] : []);
      },
    });
  }

  closeEdit(): void {
    this.editOpen.set(false);
    this.editError.set(null);
    this.editFieldErrors.set({});
    this.photoError.set(null);
  }

  onPhotoSelected(file: File): void {
    const id = this.leadId();
    if (!id) return;

    this.photoUploading.set(true);
    this.photoError.set(null);

    const req =
      this.area() === 'tenant'
        ? this.tenantLeadsService.uploadProfilePhoto(id, file)
        : this.leadsService.uploadProfilePhoto(id, file);

    req.subscribe({
      next: (updated) => {
        this.lead.set(updated);
        this.photoUploading.set(false);
        this.toast.success('Passport photo uploaded.');
      },
      error: (err) => {
        this.photoUploading.set(false);
        const msg = err?.error?.message ?? 'Could not upload photo.';
        this.photoError.set(msg);
        this.toast.error(msg);
      },
    });
  }

  removePhoto(): void {
    const id = this.leadId();
    if (!id) return;

    this.photoRemoving.set(true);
    this.photoError.set(null);

    const req =
      this.area() === 'tenant'
        ? this.tenantLeadsService.update(id, { profilePhotoMediaAssetId: '' })
        : this.leadsService.update(id, { profilePhotoMediaAssetId: '' });

    req.subscribe({
      next: (updated) => {
        this.lead.set(updated);
        this.photoRemoving.set(false);
        this.toast.success('Passport photo removed.');
      },
      error: (err) => {
        this.photoRemoving.set(false);
        const msg = err?.error?.message ?? 'Could not remove photo.';
        this.photoError.set(msg);
        this.toast.error(msg);
      },
    });
  }

  saveEdit(): void {
    const id = this.leadId();
    if (!id) return;
    this.editError.set(null);
    const fieldErrors = validateRequiredLeadFormFields(this.editForm);
    this.editFieldErrors.set(fieldErrors);
    if (hasLeadFormFieldErrors(fieldErrors)) {
      this.editError.set('Please fill in all required fields.');
      return;
    }

    this.editSaving.set(true);
    const payload: UpdateLeadPayload = leadFormToUpdatePayload(this.editForm);
    const req =
      this.area() === 'tenant'
        ? this.tenantLeadsService.update(id, payload)
        : this.leadsService.update(id, payload);
    req.subscribe({
      next: (updated) => {
        this.lead.set(updated);
        this.editSaving.set(false);
        this.editOpen.set(false);
        this.refreshTimeline();
      },
      error: (err) => {
        this.editSaving.set(false);
        this.editError.set(err?.error?.message ?? 'Failed to save lead.');
      },
    });
  }

  archiveLead(): void {
    if (!window.confirm('Archive this lead? Workflow transitions will be disabled until restored.')) return;
    this.runLeadAction((id) =>
      this.area() === 'tenant' ? this.tenantLeadsService.archive(id) : this.leadsService.archive(id),
    );
  }

  restoreLead(): void {
    if (!window.confirm('Restore this lead to active status?')) return;
    this.runLeadAction((id) =>
      this.area() === 'tenant' ? this.tenantLeadsService.unarchive(id) : this.leadsService.unarchive(id),
    );
  }

  openTransitionModal(t: LeadTransitionOption): void {
    this.pendingTransition.set({ ...t, transitionName: this.transitionLabel(t) });
    this.transitionError.set(null);
    this.transitionModalOpen.set(true);
  }

  closeTransitionModal(): void {
    this.transitionModalOpen.set(false);
    this.pendingTransition.set(null);
    this.transitionError.set(null);
  }

  openWorkflowDocumentation(): void {
    this.workflowDocOpen.set(true);
    this.workflowDocError.set(null);
    this.workflowDocLoading.set(true);
    const req =
      this.area() === 'tenant'
        ? this.tenantLeadsService.getWorkflowDocumentation()
        : this.leadsService.getWorkflowDocumentation();
    req.subscribe({
      next: (doc) => {
        this.workflowDocStatuses.set(doc.statuses ?? []);
        this.workflowDocSteps.set(doc.steps ?? []);
        this.workflowDocLoading.set(false);
      },
      error: (err) => {
        this.workflowDocStatuses.set([]);
        this.workflowDocSteps.set([]);
        this.workflowDocLoading.set(false);
        this.workflowDocError.set(err?.error?.message ?? 'Failed to load workflow documentation.');
      },
    });
  }

  closeWorkflowDocumentation(): void {
    this.workflowDocOpen.set(false);
    this.workflowDocError.set(null);
  }

  applyTransition(comment?: string): void {
    const t = this.pendingTransition();
    const id = this.leadId();
    if (!t || !id) return;
    this.actionLoading.set(true);
    this.transitionError.set(null);
    const payload = { targetStatusId: t.toStatus._id, comment: comment?.trim() || undefined };
    const req =
      this.area() === 'tenant'
        ? this.tenantLeadsService.transition(id, payload)
        : this.leadsService.transition(id, payload);
    req.subscribe({
      next: (updated) => {
        this.lead.set(updated);
        this.actionLoading.set(false);
        this.closeTransitionModal();
        this.loadTransitions(id);
        this.refreshTimeline();
      },
      error: (err) => {
        this.actionLoading.set(false);
        this.transitionError.set(err?.error?.message ?? 'Transition failed.');
      },
    });
  }

  onSubStatusChange(value: string): void {
    const id = this.leadId();
    const lead = this.lead();
    if (!id || !lead || lead.isArchived || this.subStatusDropdownDisabled()) return;

    const nextId = value || null;
    const currentId = lead.subStatusId ?? null;
    if (nextId === currentId) {
      this.selectedSubStatusId = value;
      return;
    }

    this.subStatusSaving.set(true);
    const req =
      this.area() === 'tenant'
        ? this.tenantLeadsService.updateSubStatus(id, nextId)
        : this.leadsService.updateSubStatus(id, nextId);

    req.subscribe({
      next: (updated) => {
        this.lead.set(updated);
        this.selectedSubStatusId = updated.subStatusId ?? '';
        this.subStatusSaving.set(false);
        this.refreshTimeline();
      },
      error: (err) => {
        this.selectedSubStatusId = lead.subStatusId ?? '';
        this.subStatusSaving.set(false);
        this.toast.error(err?.error?.message ?? 'Failed to update sub status.');
      },
    });
  }

  private refreshTimeline(): void {
    this.activityTimelineRef()?.reload();
  }

  onTasksChanged(): void {
    this.refreshTimeline();
  }

  openWhatsapp(): void {
    this.whatsappOpen.set(true);
  }

  closeWhatsapp(): void {
    this.whatsappOpen.set(false);
  }

  onWhatsappSent(): void {
    this.communicationsExpanded.set(true);
    this.refreshCommunications();
  }

  openEmail(): void {
    this.emailOpen.set(true);
  }

  closeEmail(): void {
    this.emailOpen.set(false);
  }

  onEmailSent(): void {
    this.communicationsExpanded.set(true);
    this.refreshCommunications();
    this.activityTimelineRef()?.reload();
  }

  refreshCommunications(): void {
    const id = this.leadId();
    if (!id) return;
    this.communicationsLoading.set(true);
    this.communicationsPage.set(1);
    this.communicationsService.getCommunications({ leadId: id, channel: 'all', page: 1, limit: 20 }).subscribe({
      next: (page) => {
        this.applyCommsPage(page, { replace: true });
        this.communicationsLoading.set(false);
        this.communicationsLoaded = true;
      },
      error: () => {
        this.communications.set([]);
        this.communicationsLoading.set(false);
        this.communicationsHasMore.set(false);
      },
    });
  }

  loadMoreCommunications(): void {
    if (this.communicationsLoadingMore() || !this.communicationsHasMore()) return;
    const id = this.leadId();
    if (!id) return;
    const nextPage = this.communicationsPage() + 1;
    this.communicationsLoadingMore.set(true);
    this.communicationsService.getCommunications({ leadId: id, channel: 'all', page: nextPage, limit: 20 }).subscribe({
      next: (page) => {
        this.applyCommsPage(page, { replace: false });
        this.communicationsLoadingMore.set(false);
      },
      error: () => {
        this.communicationsLoadingMore.set(false);
      },
    });
  }

  communicationStatusClass(status: string): string {
    return `comm-status--${status}`;
  }

  communicationStatusLabel(status: string): string {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  isEmail(log: CommunicationDto): boolean {
    return this.resolveChannel(log) === 'email';
  }

  communicationChannelLabel(log: CommunicationDto): string {
    return this.resolveChannel(log) === 'email' ? 'Email' : 'WhatsApp';
  }

  communicationChannelClass(log: CommunicationDto): string {
    return `comm-channel--${this.resolveChannel(log)}`;
  }

  private resolveChannel(log: CommunicationDto): CommunicationChannel {
    return (log.channel ?? (log.type as CommunicationChannel)) === 'email' ? 'email' : 'whatsapp';
  }

  private applyCommsPage(page: Paginated<CommunicationDto>, opts: { replace: boolean }): void {
    if (opts.replace) {
      this.communications.set(page.items);
    } else {
      this.communications.update((prev) => [...prev, ...page.items]);
    }
    this.communicationsPage.set(page.page);
    this.communicationsHasMore.set(page.hasMore);
  }

  formatSourceLabel(source: string): string {
    return source
      .replace(/[-_/]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  transitionLabel(transition: LeadTransitionOption): string {
    const name = transition.transitionName.trim().toLowerCase();
    if (name === 'lead enrolled' || transition.toStatus.code === 'ENROLLED') {
      return 'Enroll Applicant';
    }
    return transition.transitionName;
  }

  transitionIcon(transition: LeadTransitionOption): string {
    const name = this.transitionLabel(transition).toLowerCase();
    if (name.includes('reject')) return 'circle-x';
    if (
      name.includes('application') ||
      name.includes('start') ||
      name.includes('create') ||
      name.includes('convert') ||
      name.includes('enroll')
    ) {
      return 'play';
    }
    if (name.includes('reopen') || name.includes('counsel')) return 'rotate-ccw';
    return 'clipboard-check';
  }

  isRejectTransition(transition: LeadTransitionOption): boolean {
    return transition.transitionName.toLowerCase().includes('reject');
  }

  isCreateApplicantTransition(transition: LeadTransitionOption): boolean {
    return transition.toStatus.code === 'ENROLLED';
  }

  private sortWorkflowTransitions(items: LeadTransitionOption[]): LeadTransitionOption[] {
    const priority = (transition: LeadTransitionOption): number => {
      if (this.isCreateApplicantTransition(transition)) return 0;
      if (this.isRejectTransition(transition)) return 2;
      return 1;
    };

    return [...items].sort((a, b) => priority(a) - priority(b));
  }

  messagePreview(text: string): string {
    const trimmed = text.trim();
    return trimmed.length > 120 ? `${trimmed.slice(0, 120)}…` : trimmed;
  }

  statusVariant(code: string): 'published' | 'draft' {
    return code === 'COMPLETED' || code === 'DISQUALIFIED' ? 'draft' : 'published';
  }

  private ensureLeadSourceDefault(lead: LeadDto): void {
    if (lead.leadSource?.trim() || lead.isArchived) {
      return;
    }

    this.saveLeadSource('Website');
  }

  private loadLead(id: string): void {
    const req =
      this.area() === 'tenant'
        ? this.tenantLeadsService.getById(id)
        : this.leadsService.getById(id);

    req.subscribe({
      next: (data) => {
        if (data.isMerged && data.mergedIntoLeadId) {
          const base = this.area() === 'tenant' ? '/tenant/leads' : '/platform/leads';
          this.toast.success('This lead was merged. Opening the master lead.');
          void this.router.navigate([base, data.mergedIntoLeadId]);
          return;
        }
        this.lead.set(data);
        this.selectedOwnerUserId = data.ownerUserId ?? '';
        this.selectedLeadSource = resolveLeadSource(data.leadSource);
        this.loading.set(false);
        this.ensureLeadSourceDefault(data);
        this.loadTransitions(id);
        this.loadSubStatuses(data.subStatusId);
        this.checkDuplicatesForLead(id);
      },
      error: () => {
        this.lead.set(null);
        this.loading.set(false);
        this.hasDuplicateMerge.set(false);
        this.mergeLeadIds.set([]);
      },
    });
  }

  openMergeDuplicates(): void {
    if (this.mergeLeadIds().length < 2) return;
    this.mergeOpen.set(true);
  }

  closeMergeDuplicates(): void {
    this.mergeOpen.set(false);
  }

  onMergeDuplicatesCompleted(result: LeadMergeExecuteResult): void {
    this.mergeOpen.set(false);
    this.hasDuplicateMerge.set(false);
    this.mergeLeadIds.set([]);
    const masterId = result.lead._id;
    const base = this.area() === 'tenant' ? '/tenant/leads' : '/platform/leads';
    if (masterId !== this.leadId()) {
      void this.router.navigate([base, masterId]);
      return;
    }
    this.loadLead(masterId);
  }

  private checkDuplicatesForLead(leadId: string): void {
    this.hasDuplicateMerge.set(false);
    this.mergeLeadIds.set([]);

    const request =
      this.area() === 'tenant'
        ? this.tenantLeadsService.previewDuplicates()
        : this.leadsService.previewDuplicates();

    request.subscribe({
      next: (preview) => {
        const group = preview.groups.find((item) =>
          item.leads.some((lead) => lead._id === leadId),
        );
        if (!group || group.leads.length < 2) {
          this.hasDuplicateMerge.set(false);
          this.mergeLeadIds.set([]);
          return;
        }
        this.mergeLeadIds.set(group.leads.map((lead) => lead._id));
        this.hasDuplicateMerge.set(true);
      },
      error: () => {
        this.hasDuplicateMerge.set(false);
        this.mergeLeadIds.set([]);
      },
    });
  }

  private loadTransitions(id: string): void {
    this.transitionsLoading.set(true);
    const req =
      this.area() === 'tenant'
        ? this.tenantLeadsService.getTransitions(id)
        : this.leadsService.getTransitions(id);
    req.subscribe({
      next: (items) => {
        this.transitions.set(this.sortWorkflowTransitions(items));
        this.transitionsLoading.set(false);
      },
      error: () => {
        this.transitions.set([]);
        this.transitionsLoading.set(false);
      },
    });
  }

  private loadSubStatuses(currentSubStatusId?: string): void {
    this.subStatusesLoading.set(true);
    const req =
      this.area() === 'tenant'
        ? this.tenantLeadsService.listSubStatuses()
        : this.leadsService.listSubStatuses();

    req.subscribe({
      next: (items) => {
        this.subStatuses.set(items);
        this.selectedSubStatusId = currentSubStatusId ?? this.lead()?.subStatusId ?? '';
        this.subStatusesLoading.set(false);
      },
      error: () => {
        this.subStatuses.set([]);
        this.selectedSubStatusId = currentSubStatusId ?? this.lead()?.subStatusId ?? '';
        this.subStatusesLoading.set(false);
      },
    });
  }

  private runLeadAction(action: (id: string) => ReturnType<LeadsService['archive']>): void {
    const id = this.leadId();
    if (!id) return;
    this.actionLoading.set(true);
    action(id).subscribe({
      next: (updated) => {
        this.lead.set(updated);
        this.actionLoading.set(false);
        this.loadTransitions(id);
      },
      error: () => {
        this.actionLoading.set(false);
      },
    });
  }
}
